import React, {useCallback, useRef, useState} from 'react';
import {Button, Drawer, Typography} from 'antd';
import FaceDetection from "../FaceDetection/FaceDetection";
import {FaCameraRotate} from "react-icons/fa6";

const {Title} = Typography;


const SurveyFaceCameraDrawer = ({visible, onClose, onCapture, height, mode = 'photo'}) => {
    const webcamRef = useRef(null);
    const [imgSrc, setImgSrc] = useState(null);
    const [statusMessages, setStatusMessages] = useState([]); // now will display
    const [captureMode, setCaptureMode] = useState(mode); // 'photo' or 'video'
    const [countup, setCountup] = useState(null);
    const countupRef = useRef(null);
    const [facingMode, setFacingMode] = useState('user'); // 'user' for front camera, 'environment' for back camera

    const startCounter = () => {
        setCountup(0);
        countupRef.current = setInterval(() => {
            setCountup((prev) => {
                if (prev >= 12) {
                    clearInterval(countupRef.current);
                    return prev;
                }
                return prev + 1;
            });
        }, 1000);
    };

    const stopCounter = () => {
        clearInterval(countupRef.current);
        setCountup(null);
    };

    const getCountupColor = (value) => {
        if (value <= 5) return "green";
        if (value <= 10) return "yellow";
        if (value <= 12) return "orange";
        return "red";
    };

    // Update captureMode if mode prop changes
    React.useEffect(() => {
        if (visible) setCaptureMode(mode);
    }, [mode, visible]);

    // Reset all state when drawer is closed
    React.useEffect(() => {
        if (!visible) {
            setImgSrc(null);
            setStatusMessages([]);
            setIsRecording(false);
            chunksRef.current = [];
            setVideoBlob(null);
            setFacingMode('user'); // Reset to front camera
        }
    }, [visible]);

    const flipCamera = () => {
        setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    };
    const [isRecording, setIsRecording] = useState(false);
    const chunksRef = useRef([]);
    const [videoBlob, setVideoBlob] = useState(null);
    const mediaRecorderRef = useRef(null);

    // Handler to receive the clean capture function from FaceDetection
    const cleanCaptureRef = useRef(null);
    const handleCaptureSetup = useCallback((captureFunc) => {
        cleanCaptureRef.current = captureFunc;
    }, []);

    const handleStatusChange = (status) => {
        setStatusMessages(status.statusMessages);
    };

    const capture = useCallback(() => {
        // Fast capture: uses optimized clean frame function from FaceDetection (scaled, no overlay)
        // Previously depended on react-webcam getScreenshot at very high resolution causing delays on some devices.
        setCaptureMode('photo');
        if (cleanCaptureRef.current) {
            try {
                const cleanImage = cleanCaptureRef.current(); // already scaled & JPEG
                if (cleanImage) {
                    setImgSrc(cleanImage);
                    return;
                }
            } catch (error) {
                // fallback to webcam getScreenshot
            }
        }
        if (webcamRef.current) {
            try {
                const webcamImage = webcamRef.current.getScreenshot();
                setImgSrc(webcamImage);
            } catch (webcamError) {
            }
        }
    }, []);

    // Video recording logic
    const handleDataAvailable = useCallback(({data}) => {
        if (data.size > 0) {
            chunksRef.current.push(data);
        }
    }, []);

    const startRecording = useCallback(() => {
        if (!window.MediaRecorder) {
            setStatusMessages(prev => [...prev, {text: 'MediaRecorder not supported in this browser', type: 'error'}]);
            return;
        }
        setCaptureMode('video');
        chunksRef.current = [];
        setVideoBlob(null);
        if (webcamRef.current && webcamRef.current.stream) {
            const stream = webcamRef.current.stream;
            let optionsList = [
                {mimeType: 'video/webm;codecs=vp9'},
                {mimeType: 'video/webm;codecs=vp8'},
                {mimeType: 'video/webm'},
                {}
            ];
            let mediaRecorder = null;
            for (const opts of optionsList) {
                try {
                    if (opts.mimeType && !MediaRecorder.isTypeSupported(opts.mimeType)) {
                        // skip unsupported mimeType
                    } else {
                        mediaRecorder = new window.MediaRecorder(stream, opts);
                        break;
                    }
                } catch (e) {
                    // try next option
                }
            }
            if (!mediaRecorder) {
                setStatusMessages(prev => [...prev, {
                    text: 'Unable to start recording: unsupported format',
                    type: 'error'
                }]);
                return;
            }
            mediaRecorderRef.current = mediaRecorder;
            mediaRecorder.addEventListener('dataavailable', handleDataAvailable);
            mediaRecorder.addEventListener('stop', () => {
                setTimeout(() => {
                    if (chunksRef.current.length > 0) {
                        const blob = new Blob(chunksRef.current, {type: 'video/webm'});
                        setVideoBlob(blob);
                    }
                }, 100);
            });
            startCounter();
            mediaRecorder.start();
            setIsRecording(true);
        }
    }, [handleDataAvailable]);

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);

            stopCounter();
        }
    }, [isRecording]);

    const handleSave = () => {
        if (captureMode === 'photo' && imgSrc) {
            // Convert base64 to File (always save as jpg)
            const arr = imgSrc.split(','), bstr = atob(arr[1]), n = bstr.length,
                u8arr = new Uint8Array(n);
            for (let i = 0; i < n; ++i) {
                u8arr[i] = bstr.charCodeAt(i);
            }
            const file = new File([u8arr], `photo_${Date.now()}.jpg`, {type: 'image/jpeg'});
            onCapture(file);
            setImgSrc(null);
            onClose();
        } else if (captureMode === 'video' && videoBlob) {
            const file = new File([videoBlob], `video_${Date.now()}.webm`, {type: 'video/webm'});
            onCapture(file);
            setVideoBlob(null);
            chunksRef.current = [];
            onClose();
        }
    };

    const handleRetake = () => {
        setImgSrc(null);
        setVideoBlob(null);
        chunksRef.current = [];
        setIsRecording(false);
    };

    return (
        <Drawer
            title="Face Analysis Camera"
            placement="bottom"
            height={height || "90%"}
            onClose={onClose}
            destroyOnClose={true}
            open={visible}
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minHeight: "60vh",
                    maxHeight: "80vh",
                    position: 'relative',
                }}
            >
                {/* Status messages */}
                {statusMessages && statusMessages.length > 0 && (
                    <div style={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        right: 8,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 4,
                        justifyContent: 'center',
                        zIndex: 20,
                    }}>
                        {statusMessages.map((m, i) => {
                            const color = m.type === 'success' ? 'green' : m.type === 'error' ? 'red' : m.type === 'warning' ? 'orange' : '#fff';
                            return (
                                <span key={i} style={{
                                    background: '#2B2238',
                                    color,
                                    padding: '2px 8px',
                                    borderRadius: 12,
                                    fontSize: 12,
                                    border: '1px solid #3F374B'
                                }}>{m.text}</span>
                            )
                        })}
                    </div>
                )}
                {isRecording && countup !== null && (
                    <div hidden style={{textAlign: "center", marginBottom: "1rem"}}>
                        <Title
                            level={2}
                            style={{
                                margin: 0,
                                color: getCountupColor(countup),
                            }}
                        >
                            {countup}
                        </Title>

                        {countup === 12 && (
                            <p
                                style={{fontSize: "1rem", padding: "6px 12px", color: "orange"}}
                            >
                                Finalize your recording
                            </p>
                        )}
                    </div>
                )}
                {captureMode === "photo" && !imgSrc && !isRecording && !videoBlob && (
                    <>
                        <FaceDetection
                            webcamRef={webcamRef}
                            mirror={facingMode === 'user'}
                            facingMode={facingMode}
                            onStatusChange={handleStatusChange}
                            onCapture={handleCaptureSetup}
                            hide={false}
                        />
                        <div
                            style={{
                                display: "flex",
                                gap: 12,
                                marginTop: 24,
                                position: "fixed",
                                bottom: 24,
                                zIndex: 10,
                            }}
                        >
                            <Button onClick={flipCamera}>
                                <FaCameraRotate/>
                            </Button>
                            <Button type="primary" onClick={capture}>
                                Capture Photo
                            </Button>
                            {/*<Button onClick={() => startRecording()}>Record Video</Button>*/}
                        </div>
                    </>
                )}
                {captureMode === "photo" && imgSrc && (
                    <>
                        <img
                            src={imgSrc}
                            alt="captured"
                            style={{maxWidth: "100%", maxHeight: 400, marginBottom: 16}}
                        />
                        <div style={{display: "flex", gap: 8}}>
                            <Button onClick={handleRetake}>Retake</Button>
                            <Button type="primary" onClick={handleSave}>
                                Save
                            </Button>
                        </div>
                    </>
                )}
                {captureMode === "video" && !videoBlob && (
                    <>
                        <FaceDetection
                            webcamRef={webcamRef}
                            mirror={facingMode === 'user'}
                            facingMode={facingMode}
                            onStatusChange={handleStatusChange}
                            onCapture={handleCaptureSetup}
                            hide={false}
                        />
                        <div
                            style={{
                                display: "flex",
                                gap: 12,
                                marginTop: 24,
                                position: "fixed",
                                bottom: 24,
                                zIndex: 10,
                            }}
                        >
                            {!isRecording ? (
                                <Button type="primary" danger onClick={startRecording}>
                                    Start Recording
                                </Button>
                            ) : (
                                <>
                                    <Button onClick={stopRecording}>Stop Recording</Button>
                                    <Button onClick={handleRetake}>Cancel</Button>
                                </>
                            )}
                        </div>
                    </>
                )}
                {captureMode === "video" && videoBlob && (
                    <>
                        <video
                            src={URL.createObjectURL(videoBlob)}
                            controls
                            style={{maxWidth: "100%", maxHeight: 400, marginBottom: 16}}
                        />
                        <div style={{display: "flex", gap: 8}}>
                            <Button onClick={handleRetake}>Retake</Button>
                            <Button type="primary" onClick={handleSave}>
                                Save
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </Drawer>
    );
};

export default SurveyFaceCameraDrawer;
